import type { BaseNodeData, BaseEdgeData, BaseNodeType, BaseEdgeType } from 'uikit/graphs/default'
import type { OnNodesChange, OnEdgesChange, Node, Edge, XYPosition } from '@xyflow/react'

/**
 * Business Entity Types - Platform/source identifiers
 * Based on LLD: Node Types
 */
export const BusinessEntityTypes = {
  // Contact info
  EMAIL: 'EMAIL',
  LOCATION: 'LOCATION',
  PHONE: 'PHONE',
  // Identity
  IDENTITY: 'IDENTITY',
  // Platform identifiers
  AIRBNB_HOST: 'AIRBNB_HOST',
  AIRBNB_LISTING: 'AIRBNB_LISTING',
  ARKHAM: 'ARKHAM',
  BAR_BUSINESS: 'BAR_BUSINESS',
  BLOCKCHAIN_WALLET: 'BLOCKCHAIN_WALLET',
  BLUR_COLLECTION: 'BLUR_COLLECTION',
  BOOKING_HOST: 'BOOKING_HOST',
  BOOKING_LISTING: 'BOOKING_LISTING',
  BUILDZOOM: 'BUILDZOOM',
  CRS_TAX_PAYER: 'CRS_TAX_PAYER',
  DISCORD_GUILD: 'DISCORD_GUILD',
  DISCORD_INVITE: 'DISCORD_INVITE',
  ECOMMERCE_SHOP: 'ECOMMERCE_SHOP',
  ENS_DOMAIN: 'ENS_DOMAIN',
  ENS_PROFILE: 'ENS_PROFILE',
  ETSY_PEOPLE: 'ETSY_PEOPLE',
  ETSY_SHOP: 'ETSY_SHOP',
  EVM_ADDRESS: 'EVM_ADDRESS',
  FACEBOOK: 'FACEBOOK',
  FARCASTER: 'FARCASTER',
  FIVERR_PROFILE: 'FIVERR_PROFILE',
  FOURSQUARE_BUSINESS: 'FOURSQUARE_BUSINESS',
  GMAPS: 'GMAPS',
  INSTAGRAM: 'INSTAGRAM',
  LINKEDIN_COMPANY: 'LINKEDIN_COMPANY',
  LINKEDIN_PROFILE: 'LINKEDIN_PROFILE',
  LINKEDIN: 'LINKEDIN',
  LINKTREE: 'LINKTREE',
  MAGIC_EDEN_ACCOUNT: 'MAGIC_EDEN_ACCOUNT',
  MAGIC_EDEN_COLLECTION: 'MAGIC_EDEN_COLLECTION',
  MANUAL_LEAD: 'MANUAL_LEAD',
  MIDRAG: 'MIDRAG',
  ONLYFANS: 'ONLYFANS',
  OPENCORPORATES: 'OPENCORPORATES',
  OPENSEA_ACCOUNT: 'OPENSEA_ACCOUNT',
  OPENSEA_COLLECTION: 'OPENSEA_COLLECTION',
  OPENTABLE: 'OPENTABLE',
  PANDORA_PAPERS_OFFICER: 'PANDORA_PAPERS_OFFICER',
  PINTEREST_PROFILE: 'PINTEREST_PROFILE',
  REGISTRY_OFFICER_CY: 'REGISTRY_OFFICER_CY',
  REGISTRY_ORGANIZATION_CY: 'REGISTRY_ORGANIZATION_CY',
  SHOPIFY_SHOP: 'SHOPIFY_SHOP',
  STR_REGISTRY: 'STR_REGISTRY',
  STR_REGISTRY_GIS: 'STR_REGISTRY_GIS',
  TELEGRAM: 'TELEGRAM',
  TIKTOK_PROFILE: 'TIKTOK_PROFILE',
  TRIPADVISOR_BUSINESS: 'TRIPADVISOR_BUSINESS',
  TWITTER: 'TWITTER',
  UPWORK_PROFILE: 'UPWORK_PROFILE',
  VRBO_HOST: 'VRBO_HOST',
  VRBO_LISTING: 'VRBO_LISTING',
  WEB3_BIO: 'WEB3_BIO',
  WEBSITE: 'WEBSITE',
  WEEDMAPS_BUSINESS: 'WEEDMAPS_BUSINESS',
  YELP_BUSINESS: 'YELP_BUSINESS',
  YOUTUBE_CHANNEL: 'YOUTUBE_CHANNEL',
} as const

export type BusinessEntityType = (typeof BusinessEntityTypes)[keyof typeof BusinessEntityTypes]

/**
 * Contact info node types
 */
export const CONTACT_INFO_TYPES = [
  BusinessEntityTypes.LOCATION,
  BusinessEntityTypes.EMAIL,
  BusinessEntityTypes.PHONE,
] as const

export type ContactInfoType = (typeof CONTACT_INFO_TYPES)[number]

/**
 * Node Category - Distinguishes between identifier platforms and contact info
 */
export const NodeCategories = {
  DATA_SOURCE: 'data_source',
  CONTACT_INFO: 'contact_info',
  IDENTITY: 'identity',
  ASSET: 'asset',
  UNKNOWN: 'unknown',
} as const

export type NodeCategory = (typeof NodeCategories)[keyof typeof NodeCategories]

/**
 * Node Type Configuration - Unified config for each business entity type
 */
export interface NodeTypeConfig {
  category: NodeCategory
  icon: string | null
  label: string
}

/**
 * Edge Type - How the edge was created
 */
export const EdgeTypes = {
  INCLUDED: 'included',
  INFERRED: 'inferred',
} as const

export type EdgeType = (typeof EdgeTypes)[keyof typeof EdgeTypes]

/**
 * React Flow Node Type IDs - Maps to node components
 */
export const GraphNodeTypes = {
  SOURCE: 'sourceNode',
  CONTACT_INFO: 'contactInfoNode',
  IDENTITY: 'identityNode',
} as const

export type GraphNodeType = (typeof GraphNodeTypes)[keyof typeof GraphNodeTypes]

/**
 * Edge Subtype - Relationship types between nodes
 */
export const EdgeSubtypes = {
  IS_LINKED: 'is_linked',
  IDENTITY_DATA: 'identity_data',
  IDENTICAL_PHOTO: 'identical_photo',
  IDENTICAL_USERNAME: 'identical_username',
  SIMILAR_PROFILE_INFO: 'similar_profile_info',
  SOCIAL_NETWORK_OVERLAP: 'social_network_overlap',
  OVERLAPPING_GEOTAGGED_LOCATIONS: 'overlapping_geotagged_locations',
  RECURRING_CROSS_PLATFORM_LOCATIONS: 'recurring_cross_platform_locations',
  OPEN_SOURCE_MATCHING: 'open_source_matching',
  SAME_DOMAIN: 'same_domain',
  SAME_OWNER_NAME: 'same_owner_name',
  PUBLIC_LOCATION: 'public_location',
  // Crypto - ENS and wallet relationships
  SHARED_ENS_NAME_WALLET: 'shared_ens_name_wallet',
  INFERRED_SAME_ENTITY: 'inferred_same_entity',
  ONE_SIDED_TRANSFERS: 'one_sided_transfers',
  TOP_COUNTERPARTIES: 'top_counterparties',
  ROYALTY_WALLET: 'royalty_wallet',
  DEPOSIT_WALLET: 'deposit_wallet',
  FUNDED_BY_WALLET: 'funded_by_wallet',
  CONTRACT_CREATOR: 'contract_creator',
  PUBLISHED_PROFILE_ADDRESS: 'published_profile_address',
  PROFILE_PICTURE_NFT_OWNER: 'profile_picture_nft_owner',
  CIRCULAR_TRADE_PATTERN: 'circular_trade_pattern',
  // E-commerce / Etsy - company and website relationships
  PARENT_COMPANY_WEBSITE: 'parent_company_website',
  SUBSIDIARY_COMPANY_WEBSITE: 'subsidiary_company_website',
  SISTER_COMPANY_WEBSITE: 'sister_company_website',
  SAME_LOGO: 'same_logo',
} as const

export type EdgeSubtype = (typeof EdgeSubtypes)[keyof typeof EdgeSubtypes]

/**
 * Entity type for linking nodes to business entities
 */
export type EntityType = 'case' | 'lead' | 'identity'

/**
 * Address structure for location data
 */
export interface Address {
  address_line1: string
  address_line2?: string | null
  city: string
  state?: string | null
  postcode?: string | null
  country_code?: string
  /** Human-readable country name (e.g., "United States") */
  country?: string
}

/**
 * Identity containing contact and personal info
 */
export interface Identity {
  photo_url?: string
  birthday?: string
  phones?: string[]
  emails?: string[]
  urls?: string[]
  locations?: Address[]
}

/**
 * View context for frontend display - tracks which graphs a node belongs to.
 * Separate from metadata to keep original API data clean.
 */
export interface ViewContext {
  /** True if node exists in case graph */
  isCaseNode?: boolean
  /** All identity IDs this node belongs to (can be empty) */
  identityIds?: string[]
}

/**
 * STR-specific metadata fields surfaced on Host / Listing / Registry nodes.
 * Field names are the snake_case keys produced by the API normalizer
 * (backend emits them as camelCase on `GraphNode.metadata`).
 * All optional - field availability depends on node type and upstream coverage.
 */
export interface StrMetadata {
  // Host (AIRBNB_HOST, VRBO_HOST)
  first_activity?: string
  listings_count?: number
  // Listing (AIRBNB_LISTING, VRBO_LISTING, BOOKING_LISTING)
  is_active?: boolean
  property_type?: string
  reviews_count?: number
  // Registry (STR_REGISTRY, STR_REGISTRY_GIS)
  description?: string
  license_status?: string
  // Shared across Listing + Registry
  host_name?: string
  owner_name?: string
  license_num?: string
  // Set on every STR data-source node by the graph builder
  platform?: string
}

/**
 * Node metadata - combined type containing address, identity and vertical-specific fields
 * All fields are optional since different node types use different subsets
 */
export interface NodeMetadata extends Partial<Address>, Partial<Identity>, StrMetadata {}

/**
 * API Node format from backend (LLD schema)
 */
export interface ApiNode {
  /** Unique composite identifier (e.g., "evm_address:0x791f...") */
  id: string
  /** Raw identifier (URL, wallet address, phone, etc.) */
  identifier_key: string
  /** Business entity type (WEBSITE, FACEBOOK, PHONE, etc.) */
  type: BusinessEntityType
  /** IDENTIFIER or CONTACT_INFO */
  category: NodeCategory
  /** Human-readable name for UI */
  display_name: string | null
  /** Priority for sorting/display */
  priority?: number
  /** Saved node position for layout persistence */
  position?: XYPosition
  /** Additional metadata */
  metadata?: NodeMetadata
  /** View context to help the client understand node ownership (case/identity etc) */
  view_context?: ViewContext
}

/**
 * API Edge format from backend (LLD schema)
 */
export interface ApiEdge {
  /** Composite id of the source node */
  source_id: string
  /** Composite id of the target node */
  target_id: string
  /** How edge was created (INCLUDED or INFERRED) */
  type: EdgeType
  /** Relationship subtype */
  sub_type: EdgeSubtype
  /** Identity id that is connected to the case */
  external_id?: string
}

/**
 * API Graph Data structure (LLD schema)
 */
export interface ApiGraphData {
  nodes: ApiNode[]
  edges: ApiEdge[]
}

/**
 * Raw API response format (camelCase from the backend)
 */
export interface RawApiNode {
  id: string
  identifierKey: string
  type: string
  category: string
  displayName: string | null
  priority?: number
  position?: { x: number; y: number }
  metadata?: Record<string, unknown>
}

export interface RawApiEdge {
  sourceId: string
  targetId: string
  type: string
  subType: string
  externalId?: string
}

export interface RawApiGraphData {
  nodes: RawApiNode[]
  edges: RawApiEdge[]
}

/**
 * Network Node Data - Extended node data for React Flow
 */
export interface NetworkNodeData extends BaseNodeData {
  /** The identifier_key from API */
  identifierKey: string
  /** Business entity type */
  nodeType: BusinessEntityType
  /** Node category (IDENTIFIER or CONTACT_INFO) */
  nodeCategory: NodeCategory
  /** Human-readable display name */
  displayName: string | null
  /** Priority for sorting/display */
  priority?: number
  /** Whether the node is disabled (excluded from output) */
  disabled?: boolean
  /** Whether the node is readonly (visible but non-interactive, faded appearance) */
  readonly?: boolean
  /** Whether the node can have connections created from/to it. Defaults to true. */
  isConnectable?: boolean
  /** Additional metadata */
  metadata?: NodeMetadata
  /** View context with ownership info (separate from metadata, camelCase for frontend) */
  viewContext?: ViewContext
}

/**
 * Network Edge Data - Extended edge data for React Flow
 */
export interface NetworkEdgeData extends BaseEdgeData {
  /** Edge type (INCLUDED or INFERRED) */
  type: EdgeType
  /** Edge subtype (relationship type) */
  subtype: EdgeSubtype
  /** Whether the edge is disabled (excluded from output) */
  disabled?: boolean
  /** Whether the edge is readonly (visible but non-interactive, faded appearance) */
  readonly?: boolean
  /** Identity ID for cross-boundary edges (when edge connects to identity graph nodes) */
  externalId?: string
}

/**
 * Network Node - React Flow node with network data
 */
export type NetworkNode = BaseNodeType<NetworkNodeData>

/**
 * Network Edge - React Flow edge with network data
 */
export type NetworkEdge = BaseEdgeType<NetworkEdgeData>

/**
 * UseGraphModel hook return type
 */
export interface UseGraphModelReturn {
  nodes: NetworkNode[]
  edges: NetworkEdge[]
  onNodesChange: OnNodesChange<NetworkNode>
  onEdgesChange: OnEdgesChange<NetworkEdge>
  toggleNodeDisabled: (nodeId: string) => void
  getApiData: () => ApiGraphData
  addNode: (node: NetworkNode) => void
  addEdge: (edge: NetworkEdge) => void
  updateNode: (nodeId: string, data: Partial<NetworkNodeData>) => void
  updateEdge: (edgeId: string, data: Partial<NetworkEdgeData>) => void
  removeNode: (nodeId: string) => void
  removeEdge: (edgeId: string) => void
}

/**
 * UseGraphModel hook options
 */
export interface UseGraphModelOptions {
  initialData?: ApiGraphData
  onChange?: (data: ApiGraphData) => void
  /** Node types that should be marked as readonly (e.g., ["LOCATION"]) */
  readonlyNodeTypes?: BusinessEntityType[]
}

/**
 * Node option for search/select dropdowns
 */
export interface NodeOption {
  id: string
  identifier: string
  displayName: NetworkNodeData['displayName']
  nodeType: NetworkNodeData['nodeType']
}

/**
 * Graph view state - return type for useGraphView hook
 * Extensible for future view concerns (hover, focus, zoom, filters, etc.)
 */
export interface GraphViewState {
  selectedNode: NetworkNode | null
  selectedEdge: NetworkEdge | null
  isDrawerOpen: boolean
  edgesWithSelection: NetworkEdge[]
  nodeOptions: NodeOption[]
  handleNodeClick: (event: React.MouseEvent, node: Node) => void
  handleEdgeClick: (event: React.MouseEvent, edge: Edge) => void
  handleDrawerToggle: () => void
  handleNodeSelect: (nodeId: string) => void
  handlePaneClick: () => void
  /** Handler for React Flow's native selection changes (multi-select support) */
  onSelectionChange: import('@xyflow/react').OnSelectionChangeFunc
}
